import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DonkeyGuide } from "@/components/DonkeyMascot";
import { ScoreRing } from "@/components/DonkeyBits";
import { toast } from "sonner";
import {
  Loader2, TrendingUp, Users, KanbanSquare, CalendarClock, Gauge, ArrowLeft, Plus, Trash2,
  Download, Upload, RefreshCw, AlertTriangle, CheckCircle2, Flame, UserCheck, Workflow, Anchor, Bot, FlaskConical,
} from "lucide-react";

const TABS = [
  ["overview", "Command Centre", TrendingUp],
  ["pipeline", "Pipeline", KanbanSquare],
  ["contacts", "Contacts", Users],
  ["tasks", "Follow-ups", CalendarClock],
  ["customers", "Customers", UserCheck],
  ["delivery", "Delivery", Workflow],
  ["founder", "Founder Reliance", Anchor],
  ["analytics", "Analytics & Health", Gauge],
  ["readiness", "Growth Readiness", Gauge],
];
const STAGE_LABELS = {
  new: "New", contact_attempted: "Contact attempted", contacted: "Contacted", qualified: "Qualified",
  proposal_sent: "Proposal sent", considering: "Considering", won: "Won", lost: "Lost",
  not_suitable: "Not suitable", nurture: "Nurture", unsubscribed: "Unsubscribed",
};
const Card = ({ children, className = "", ...p }) => (
  <div className={`card-donkey rounded-2xl p-6 ${className}`} {...p}>{children}</div>
);
const prColor = (p) => p === "high" ? "text-donkey-orange border-donkey-orange/40"
  : p === "medium" ? "text-donkey-amber border-donkey-amber/40" : "text-donkey-grey border-white/20";

export default function GrowWorkspace() {
  const { bid } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [gate, setGate] = useState(null);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const loadState = useCallback(async () => {
    const { data } = await api.get(`/grow/${bid}`);
    setState(data);
  }, [bid]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/grow/eligibility/${bid}`);
        setGate(data);
        if (data.can_enter && data.grow_started) await loadState();
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load Grow."); }
      finally { setLoading(false); }
    })();
  }, [bid, loadState]);

  const start = async () => {
    setBusy(true);
    try { await api.post(`/grow/${bid}/start`); await loadState(); setGate((g) => ({ ...g, grow_started: true })); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-donkey-bg flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-donkey-orange" /></div>;

  if (!gate?.can_enter) {
    return (
      <div className="min-h-screen bg-donkey-bg grain">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 py-12">
          <button onClick={() => navigate(`/launch/${bid}`)} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5 mb-6" data-testid="grow-back-btn"><ArrowLeft className="w-4 h-4" /> Back to Launch</button>
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-6 h-6 text-donkey-orange" /><h1 className="font-heading font-extrabold text-3xl text-donkey-warm">Stage 5 — Grow & Scale</h1></div>
          <DonkeyGuide title="Not ready to grow yet" tone="warning" className="mt-6" data-testid="grow-gate-blocked">
            To open the growth operating system you first need a launched business: a validated opportunity, a completed
            verdict and decision, a Blueprint, an offer and price, a published website with a working lead-capture route,
            a Stage 4 launch strategy and some acquisition activity. Finish these in Launch first.
          </DonkeyGuide>
          <div className="mt-4 grid sm:grid-cols-2 gap-2">
            {gate && Object.entries(gate.gate).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2 text-sm card-donkey rounded-xl p-3" data-testid={`grow-gate-${k}`}>
                {v ? <CheckCircle2 className="w-4 h-4 text-donkey-green" /> : <AlertTriangle className="w-4 h-4 text-donkey-amber" />}
                <span className="text-donkey-warm/85 capitalize">{k.replace(/_/g, " ")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!gate.grow_started || !state) {
    return (
      <div className="min-h-screen bg-donkey-bg grain">
        <div className="mx-auto max-w-2xl px-5 sm:px-8 py-16 text-center">
          <TrendingUp className="w-12 h-12 text-donkey-orange mx-auto mb-6" />
          <h1 className="font-heading font-extrabold text-3xl text-donkey-warm">Grow {gate.name}</h1>
          <p className="mt-3 text-donkey-grey">Turn your launched business into an organised operating system — manage leads, convert customers, deliver reliably and grow without losing control.</p>
          {gate.limited_mode && <p className="mt-3 text-sm text-donkey-amber">{gate.limited_reason}</p>}
          <Button onClick={start} disabled={busy} className="mt-8 bg-donkey-orange text-donkey-black font-semibold h-12 px-8" data-testid="start-grow-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Open the growth centre <TrendingUp className="w-4 h-4 ml-2" /></>}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-donkey-bg grain text-donkey-warm overflow-x-hidden">
      <div className="glass border-b border-white/10 sticky top-0 z-30">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <button onClick={() => navigate(`/launch/${bid}`)} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5" data-testid="grow-back-btn"><ArrowLeft className="w-4 h-4" /> Launch</button>
          <div className="flex items-center gap-2 min-w-0"><TrendingUp className="w-5 h-5 text-donkey-orange shrink-0" /><span className="font-heading font-semibold truncate">{state.name} · Grow</span></div>
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={() => navigate(`/growth/${bid}`)} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5" data-testid="go-to-growth-btn"><FlaskConical className="w-4 h-4" /> Experiments</button>
            <button onClick={() => navigate(`/brandrep/${bid}`)} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5" data-testid="go-to-brandrep-btn"><Bot className="w-4 h-4" /> Brand Rep</button>
            <button onClick={() => navigate("/businesses")} className="text-sm text-donkey-grey hover:text-donkey-orange flex items-center gap-1.5" data-testid="go-to-team-btn"><Users className="w-4 h-4" /> Team</button>
            <span className="text-xs rounded-full border border-donkey-orange/40 text-donkey-orange px-3 py-1 capitalize" data-testid="grow-phase-badge">{(state.phase || "").replace(/_/g, " ")}</span>
          </div>
        </div>
        <div className="mx-auto max-w-6xl px-3 sm:px-6 overflow-x-auto" role="region" aria-label="Grow sections" tabIndex={0}>
          <div className="flex gap-1 py-2 min-w-max" role="tablist" aria-label="Grow sections">
            {TABS.map(([id, label, Icon]) => (
              <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 text-xs px-3 py-2 rounded-lg whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-donkey-orange/70 ${tab === id ? "bg-donkey-orange text-donkey-black font-semibold" : "text-donkey-grey hover:text-donkey-warm hover:bg-white/5"}`}
                data-testid={`grow-tab-${id}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-6xl px-5 sm:px-8 py-8">
        {tab === "overview" && <Overview bid={bid} state={state} reload={loadState} />}
        {tab === "pipeline" && <Pipeline bid={bid} reload={loadState} />}
        {tab === "contacts" && <Contacts bid={bid} reload={loadState} />}
        {tab === "tasks" && <Tasks bid={bid} reload={loadState} />}
        {tab === "customers" && <Customers bid={bid} reload={loadState} />}
        {tab === "delivery" && <Delivery bid={bid} />}
        {tab === "founder" && <Founder bid={bid} />}
        {tab === "analytics" && <Analytics bid={bid} />}
        {tab === "readiness" && <Readiness bid={bid} />}
      </div>
    </div>
  );
}

function Overview({ state, reload }) {
  return (
    <div className="space-y-6" data-testid="grow-overview">
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="text-center"><ScoreRing value={state.readiness_score} size={110} /><p className="text-sm text-donkey-grey mt-3">Growth readiness</p>
          <p className={`text-xs mt-1 capitalize ${state.health === "healthy" ? "text-donkey-green" : "text-donkey-amber"}`}>{state.health.replace(/_/g, " ")}</p>
        </Card>
        <Card className="lg:col-span-2">
          <DonkeyGuide title="What the Donkey sees" tone={state.won === 0 ? "caution" : "positive"}>{state.next_action}</DonkeyGuide>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
            {[["Leads", state.leads_total], ["Won", state.won], ["Customers", state.customers], ["Conversion", state.conversion_rate + "%"]].map(([l, v]) => (
              <div key={l} className="rounded-xl bg-donkey-bg border border-white/10 p-3 text-center" data-testid={`grow-kpi-${l.toLowerCase()}`}>
                <p className="font-mono text-2xl text-donkey-warm">{v}</p><p className="text-xs text-donkey-grey">{l}</p>
              </div>
            ))}
          </div>
        </Card>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-3">Operating snapshot</p>
          {[["Open tasks", state.open_tasks], ["Follow-ups due", state.followups_due], ["Onboarding active", state.onboarding_active], ["Support open", state.support_open], ["Active campaigns", state.active_campaigns]].map(([l, v]) => (
            <div key={l} className="flex justify-between text-sm py-1.5 border-b border-white/5"><span className="text-donkey-grey">{l}</span><span className="text-donkey-warm font-mono">{v}</span></div>
          ))}
        </Card>
        <Card>
          <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-3">Signals</p>
          <div className="text-sm space-y-2">
            <div><span className="text-donkey-grey">Recurring revenue: </span><span className="text-donkey-warm">{state.recurring_revenue.value ?? "Not available"}</span> {state.recurring_revenue.note && <span className="text-[11px] text-donkey-grey block">{state.recurring_revenue.note}</span>}</div>
            <div><span className="text-donkey-grey">Founder dependency: </span><span className="text-donkey-amber capitalize">{state.founder_dependency.value}</span></div>
            <div>
              <span className="text-donkey-grey">Concentration risks:</span>
              {state.concentration_risks?.length ? <ul className="mt-1 space-y-1">{state.concentration_risks.map((r, i) => <li key={i} className="text-xs text-donkey-amber flex gap-1.5"><Flame className="w-3.5 h-3.5 shrink-0" />{r}</li>)}</ul> : <span className="text-donkey-green"> none flagged</span>}
            </div>
          </div>
        </Card>
      </div>
      {state.conditions_before_scaling?.length > 0 && (
        <Card data-testid="grow-conditions"><p className="text-xs uppercase tracking-[0.18em] text-donkey-orange mb-2">Before scaling</p>
          <ul className="space-y-1.5">{state.conditions_before_scaling.map((c, i) => <li key={i} className="text-sm text-donkey-warm/85 flex gap-2"><AlertTriangle className="w-4 h-4 text-donkey-amber shrink-0 mt-0.5" />{c}</li>)}</ul>
        </Card>
      )}
      <Button variant="ghost" onClick={reload} className="text-donkey-grey border border-white/10" data-testid="grow-refresh"><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
    </div>
  );
}

function usePipeline(bid) {
  const [data, setData] = useState(null);
  const load = useCallback(async () => { const { data } = await api.get(`/grow/${bid}/contacts`); setData(data); }, [bid]);
  useEffect(() => { load(); }, [load]);
  return [data, load];
}

function Pipeline({ bid, reload }) {
  const [data, load] = usePipeline(bid);
  const [importing, setImporting] = useState(false);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  const move = async (c, stage) => { try { await api.put(`/grow/${bid}/contacts/${c.id}`, { stage }); await load(); await reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const importLeads = async () => { setImporting(true); try { const { data } = await api.post(`/grow/${bid}/crm/import-leads`); toast.success(`Imported ${data.imported}, skipped ${data.skipped_duplicates} duplicates`); await load(); await reload(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setImporting(false); } };
  return (
    <div className="space-y-4" data-testid="grow-pipeline">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <p className="text-sm text-donkey-grey">Drag-free board — move a contact with its stage dropdown.</p>
        <Button onClick={importLeads} disabled={importing} className="bg-donkey-orange text-donkey-black font-semibold h-9" data-testid="import-leads-btn">{importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-2" /> Import website leads</>}</Button>
      </div>
      <div className="overflow-x-auto" role="region" aria-label="Pipeline board" tabIndex={0}>
        <div className="flex gap-3 min-w-max pb-2">
          {data.stages.map((s) => (
            <div key={s} className="w-64 shrink-0" data-testid={`pipeline-col-${s}`}>
              <div className="text-xs uppercase tracking-wide text-donkey-grey mb-2 flex justify-between"><span>{STAGE_LABELS[s]}</span><span className="text-donkey-orange">{data.board[s].length}</span></div>
              <div className="space-y-2">
                {data.board[s].map((c) => (
                  <div key={c.id} className="card-donkey rounded-xl p-3" data-testid={`pipeline-card-${c.id}`}>
                    <div className="flex justify-between items-start gap-2"><p className="text-sm font-medium truncate">{c.name}</p><span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full border ${prColor(c.priority_info?.priority)}`}>{c.priority_info?.priority}</span></div>
                    {c.estimated_value > 0 && <p className="text-xs text-donkey-grey">£{c.estimated_value}</p>}
                    <p className="text-[11px] text-donkey-grey mt-1">{c.priority_info?.recommended_next_action}</p>
                    <select value={c.stage} onChange={(e) => move(c, e.target.value)} className="mt-2 w-full bg-donkey-bg border border-white/15 rounded-lg px-2 py-1 text-xs" aria-label={`Stage for ${c.name}`} data-testid={`pipeline-stage-${c.id}`}>
                      {data.stages.map((x) => <option key={x} value={x}>{STAGE_LABELS[x]}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Contacts({ bid, reload }) {
  const [data, load] = usePipeline(bid);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", email: "", estimated_value: 0, interest: "" });
  const search = async () => { const { data } = await api.get(`/grow/${bid}/contacts`, { params: { q } }); setDataLocal(data); };
  const [local, setDataLocal] = useState(null);
  const list = local || data;
  const create = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    try { const { data } = await api.post(`/grow/${bid}/contacts`, form); if (data.duplicate_warning) toast.warning("A contact with this email already exists"); else toast.success("Contact added"); setForm({ name: "", email: "", estimated_value: 0, interest: "" }); await load(); setDataLocal(null); await reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (c) => { await api.delete(`/grow/${bid}/contacts/${c.id}`); await load(); setDataLocal(null); await reload(); };
  const exportCsv = () => { window.open(`${api.defaults.baseURL}/grow/${bid}/contacts/export`, "_blank"); };
  if (!list) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  return (
    <div className="space-y-4" data-testid="grow-contacts">
      <Card>
        <div className="grid sm:grid-cols-4 gap-3">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name" aria-label="Contact name" className="bg-donkey-bg border-white/15" data-testid="contact-name-input" />
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" aria-label="Contact email" className="bg-donkey-bg border-white/15" data-testid="contact-email-input" />
          <Input type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: Number(e.target.value) })} placeholder="Value £" aria-label="Estimated value" className="bg-donkey-bg border-white/15" />
          <Input value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} placeholder="Interest" aria-label="Interest" className="bg-donkey-bg border-white/15" />
        </div>
        <div className="flex gap-2 mt-3">
          <Button onClick={create} className="bg-donkey-orange text-donkey-black font-semibold" data-testid="add-contact-btn"><Plus className="w-4 h-4 mr-2" /> Add contact</Button>
          <Button variant="ghost" onClick={exportCsv} className="border border-white/10 text-donkey-warm" data-testid="export-contacts-btn"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
        </div>
      </Card>
      <div className="flex gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, notes…" aria-label="Search contacts" className="bg-donkey-bg border-white/15" data-testid="contact-search-input" />
        <Button variant="ghost" onClick={search} className="border border-white/10 text-donkey-warm" data-testid="contact-search-btn">Search</Button>
      </div>
      <div className="space-y-2" data-testid="contact-list">
        {list.contacts.length === 0 && <p className="text-sm text-donkey-grey">No contacts. Import your website leads from the Pipeline tab.</p>}
        {list.contacts.map((c) => (
          <Card key={c.id} className="!p-4" data-testid={`contact-${c.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0"><p className="font-medium text-sm">{c.name} <span className="text-donkey-grey">· {STAGE_LABELS[c.stage]}</span></p><p className="text-xs text-donkey-grey">{c.email} {c.estimated_value > 0 ? "· £" + c.estimated_value : ""}</p></div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full border ${prColor(c.priority_info?.priority)}`} data-testid={`contact-priority-${c.id}`}>{c.priority_info?.priority} · {c.priority_info?.confidence}</span>
                <button onClick={() => del(c)} className="text-donkey-grey hover:text-donkey-red" aria-label="Delete contact" data-testid={`delete-contact-${c.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <p className="text-[11px] text-donkey-grey mt-1.5">Why: {c.priority_info?.reasons?.join(" · ") || "—"}{c.priority_info?.missing_information?.length ? ` · Missing: ${c.priority_info.missing_information.join(", ")}` : ""}</p>
            <p className="text-[11px] text-donkey-orange mt-0.5">Next: {c.priority_info?.recommended_next_action}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}

function Tasks({ bid, reload }) {
  const [data, setData] = useState(null);
  const [view, setView] = useState("all");
  const [form, setForm] = useState({ title: "", due_date: "" });
  const load = useCallback(async () => { const { data } = await api.get(`/grow/${bid}/tasks`, { params: { view } }); setData(data); }, [bid, view]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { if (!form.title.trim()) return; await api.post(`/grow/${bid}/tasks`, { title: form.title, due_date: form.due_date || null }); setForm({ title: "", due_date: "" }); await load(); await reload(); };
  const done = async (t) => { await api.put(`/grow/${bid}/tasks/${t.id}`, { status: "done" }); await load(); await reload(); };
  const del = async (t) => { await api.delete(`/grow/${bid}/tasks/${t.id}`); await load(); await reload(); };
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  return (
    <div className="space-y-4" data-testid="grow-tasks">
      {data.missed_warning && <DonkeyGuide title="You have overdue follow-ups" tone="caution" data-testid="missed-warning">{data.counts.overdue} follow-up(s) are overdue. Working leads promptly (without pestering) is where deals are won or lost.</DonkeyGuide>}
      <Card>
        <div className="grid sm:grid-cols-3 gap-3">
          <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Follow-up task" aria-label="Task title" className="bg-donkey-bg border-white/15 sm:col-span-2" data-testid="task-title-input" />
          <Input type="datetime-local" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value ? new Date(e.target.value).toISOString() : "" })} aria-label="Due date" className="bg-donkey-bg border-white/15" data-testid="task-due-input" />
        </div>
        <Button onClick={create} className="mt-3 bg-donkey-orange text-donkey-black font-semibold" data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" /> Add follow-up</Button>
      </Card>
      <div className="flex gap-1.5">{["all", "today", "week", "overdue"].map((v) => <button key={v} onClick={() => setView(v)} className={`text-xs px-3 py-1.5 rounded-full border capitalize ${view === v ? "border-donkey-orange text-donkey-orange" : "border-white/15 text-donkey-grey"}`} data-testid={`task-view-${v}`}>{v} {v !== "all" && `(${data.counts[v] ?? 0})`}</button>)}</div>
      <div className="space-y-2" data-testid="task-list">
        {data.tasks.length === 0 && <p className="text-sm text-donkey-grey">No follow-ups in this view.</p>}
        {data.tasks.map((t) => {
          const overdue = t.status !== "done" && t.due_date && t.due_date < new Date().toISOString();
          return (
            <Card key={t.id} className="!p-4 flex items-center justify-between gap-3" data-testid={`task-${t.id}`}>
              <div className="min-w-0"><p className={`text-sm ${t.status === "done" ? "line-through text-donkey-grey" : ""}`}>{t.title}</p><p className={`text-xs ${overdue ? "text-donkey-red" : "text-donkey-grey"}`}>{t.due_date ? new Date(t.due_date).toLocaleString() : "no due date"}{overdue ? " · overdue" : ""}</p></div>
              <div className="flex items-center gap-2">
                {t.status !== "done" && <Button size="sm" onClick={() => done(t)} className="bg-donkey-green/20 text-donkey-green border border-donkey-green/40 h-8" data-testid={`task-done-${t.id}`}>Done</Button>}
                <button onClick={() => del(t)} className="text-donkey-grey hover:text-donkey-red" aria-label="Delete task" data-testid={`delete-task-${t.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Readiness({ bid }) {
  const [data, setData] = useState(null);
  useEffect(() => { (async () => { const { data } = await api.get(`/grow/${bid}/readiness`); setData(data); })(); }, [bid]);
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  const block = (title, items, tone) => items?.length > 0 && (
    <Card data-testid={`readiness-${title.toLowerCase().replace(/[^a-z]/g, "")}`}>
      <p className={`text-xs uppercase tracking-[0.18em] mb-2 ${tone}`}>{title}</p>
      <div className="flex flex-wrap gap-1.5">{items.map((x, i) => <span key={i} className="text-xs rounded-full border border-white/15 bg-donkey-bg px-2.5 py-1 text-donkey-warm/85">{x}</span>)}</div>
    </Card>
  );
  return (
    <div className="space-y-4" data-testid="grow-readiness">
      <Card className="text-center"><ScoreRing value={data.score} size={110} /><p className="text-sm text-donkey-grey mt-3">Evidence-supported readiness</p></Card>
      {block("Evidence-supported strengths", data.strengths, "text-donkey-green")}
      {block("Assumptions (not yet proven)", data.assumptions, "text-donkey-amber")}
      {block("Unknowns", data.unknowns, "text-donkey-grey")}
      {block("Immediate blockers", data.blockers, "text-donkey-red")}
      {data.conditions_before_scaling?.length > 0 && block("Conditions required before scaling", data.conditions_before_scaling, "text-donkey-orange")}
      <p className="text-xs text-donkey-grey">Readiness reflects only evidence recorded in the platform. It never assumes success or infers satisfaction from silence.</p>
    </div>
  );
}

function Customers({ bid, reload }) {
  const [items, setItems] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const load = useCallback(async () => { const { data } = await api.get(`/grow/${bid}/customers`); setItems(data.customers); setStatuses(data.statuses); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const applyOb = async (c) => { try { await api.post(`/grow/${bid}/customers/${c.id}/onboarding`, { template: "service" }); await load(); toast.success("Onboarding checklist applied"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleStep = async (c, s) => { await api.put(`/grow/${bid}/customers/${c.id}/onboarding/step`, { step_id: s.id, done: !s.done }); await load(); await reload(); };
  const setField = async (c, patch) => { await api.put(`/grow/${bid}/customers/${c.id}`, patch); await load(); await reload(); };
  if (!items) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  return (
    <div className="space-y-3" data-testid="grow-customers">
      <p className="text-sm text-donkey-grey">Customers are created only when a lead is marked <b>Won</b> — never from mere interest.</p>
      {items.length === 0 && <p className="text-sm text-donkey-grey">No customers yet. Move a qualified contact to Won in the Pipeline.</p>}
      {items.map((c) => (
        <Card key={c.id} data-testid={`customer-${c.id}`}>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div><p className="font-medium">{c.name}</p><p className="text-xs text-donkey-grey">£{c.agreed_value} · {c.purchased_offer || "—"}</p></div>
            <div className="flex items-center gap-2">
              <select value={c.status} onChange={(e) => setField(c, { status: e.target.value })} className="bg-donkey-bg border border-white/15 rounded-lg px-2 py-1 text-xs" aria-label="Customer status" data-testid={`customer-status-${c.id}`}>
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select value={c.health} onChange={(e) => setField(c, { health: e.target.value })} className="bg-donkey-bg border border-white/15 rounded-lg px-2 py-1 text-xs" aria-label="Customer health" data-testid={`customer-health-${c.id}`}>
                {["unknown", "healthy", "attention_required", "at_risk"].map((h) => <option key={h} value={h}>{h.replace(/_/g, " ")}</option>)}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 mt-3 text-xs text-donkey-grey"><input type="checkbox" checked={!!c.revenue_verified} onChange={(e) => setField(c, { revenue_verified: e.target.checked })} data-testid={`customer-revverified-${c.id}`} /> Revenue verified (you confirm this manually — no payment provider is connected)</label>
          {c.onboarding ? (
            <div className="mt-3">
              <p className="text-xs uppercase tracking-wide text-donkey-orange mb-1.5">Onboarding · {c.onboarding_stage?.replace(/_/g, " ")}</p>
              <div className="space-y-1">
                {c.onboarding.checklist.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm" data-testid={`onboarding-step-${s.id}`}>
                    <input type="checkbox" checked={s.done} onChange={() => toggleStep(c, s)} /> <span className={s.done ? "line-through text-donkey-grey" : "text-donkey-warm/85"}>{s.label}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : <Button size="sm" onClick={() => applyOb(c)} className="mt-3 bg-donkey-orange text-donkey-black h-8" data-testid={`apply-onboarding-${c.id}`}>Apply onboarding</Button>}
        </Card>
      ))}
    </div>
  );
}

function Delivery({ bid }) {
  const [items, setItems] = useState(null);
  const [form, setForm] = useState({ name: "", steps: "" });
  const load = useCallback(async () => { const { data } = await api.get(`/grow/${bid}/delivery/workflows`); setItems(data.workflows); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const create = async () => { if (!form.name.trim()) return; await api.post(`/grow/${bid}/delivery/workflows`, { name: form.name, steps: form.steps.split("\n").filter(Boolean) }); setForm({ name: "", steps: "" }); await load(); toast.success("Delivery workflow created"); };
  const dup = async (w) => { await api.post(`/grow/${bid}/delivery/workflows/${w.id}/duplicate`); await load(); };
  const del = async (w) => { await api.delete(`/grow/${bid}/delivery/workflows/${w.id}`); await load(); };
  if (!items) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  return (
    <div className="space-y-3" data-testid="grow-delivery">
      <Card>
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">New delivery workflow (SOP)</p>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Workflow name" aria-label="Workflow name" className="bg-donkey-bg border-white/15 mb-2" data-testid="workflow-name-input" />
        <textarea value={form.steps} onChange={(e) => setForm({ ...form, steps: e.target.value })} placeholder="One step per line" aria-label="Workflow steps" className="w-full bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm" rows={4} data-testid="workflow-steps-input" />
        <Button onClick={create} className="mt-2 bg-donkey-orange text-donkey-black font-semibold" data-testid="create-workflow-btn"><Plus className="w-4 h-4 mr-2" /> Create</Button>
      </Card>
      {items.map((w) => (
        <Card key={w.id} data-testid={`workflow-${w.id}`}>
          <div className="flex items-center justify-between"><p className="font-medium">{w.name}</p><div className="flex gap-2"><button onClick={() => dup(w)} className="text-xs text-donkey-grey hover:text-donkey-orange" data-testid={`dup-workflow-${w.id}`}>Duplicate</button><button onClick={() => del(w)} className="text-donkey-grey hover:text-donkey-red" aria-label="Delete workflow"><Trash2 className="w-4 h-4" /></button></div></div>
          <ol className="mt-2 space-y-1 list-decimal list-inside text-sm text-donkey-warm/85">{w.steps.map((s) => <li key={s.id}>{s.label}</li>)}</ol>
        </Card>
      ))}
    </div>
  );
}

function Founder({ bid }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ task: "", classification: "founder_only", customer_facing: false, stops_revenue: false });
  const load = useCallback(async () => { const { data } = await api.get(`/grow/${bid}/founder-dependency`); setData(data); }, [bid]);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!form.task.trim()) return; await api.post(`/grow/${bid}/founder-dependency/tasks`, form); setForm({ task: "", classification: "founder_only", customer_facing: false, stops_revenue: false }); await load(); };
  const del = async (t) => { await api.delete(`/grow/${bid}/founder-dependency/tasks/${t.id}`); await load(); };
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  return (
    <div className="space-y-4" data-testid="grow-founder">
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="text-center"><ScoreRing value={data.dependency_score} size={100} /><p className="text-sm text-donkey-grey mt-3">Founder dependency</p></Card>
        <Card className="lg:col-span-2">
          {data.recommendation ? <DonkeyGuide title="Safest next step" tone="caution"><b className="capitalize">{data.recommendation.action.replace(/_/g, " ")}:</b> {data.recommendation.task}. {data.recommendation.why}</DonkeyGuide> : <p className="text-sm text-donkey-grey">Log the tasks only you can do to see where the business depends on you.</p>}
          {data.revenue_stops_without_founder?.length > 0 && <p className="text-xs text-donkey-red mt-3">Revenue stops without you: {data.revenue_stops_without_founder.join(", ")}</p>}
        </Card>
      </div>
      <Card>
        <div className="grid sm:grid-cols-2 gap-2">
          <Input value={form.task} onChange={(e) => setForm({ ...form, task: e.target.value })} placeholder="Task only you do" aria-label="Founder task" className="bg-donkey-bg border-white/15" data-testid="founder-task-input" />
          <select value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })} className="bg-donkey-bg border border-white/15 rounded-lg px-3 py-2 text-sm" aria-label="Task classification" data-testid="founder-class-select">
            {data.classes.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="flex gap-4 mt-2 text-xs text-donkey-grey">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.customer_facing} onChange={(e) => setForm({ ...form, customer_facing: e.target.checked })} /> Customer-facing</label>
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={form.stops_revenue} onChange={(e) => setForm({ ...form, stops_revenue: e.target.checked })} /> Revenue stops without me</label>
        </div>
        <Button onClick={add} className="mt-3 bg-donkey-orange text-donkey-black font-semibold" data-testid="add-founder-task-btn"><Plus className="w-4 h-4 mr-2" /> Add task</Button>
      </Card>
      {data.classes.map((cls) => data.by_class[cls]?.length > 0 && (
        <Card key={cls} data-testid={`founder-class-${cls}`}>
          <p className="text-xs uppercase tracking-[0.18em] text-donkey-orange mb-2 capitalize">{cls.replace(/_/g, " ")}</p>
          <div className="space-y-1.5">{data.by_class[cls].map((t) => <div key={t.id} className="flex justify-between text-sm"><span className="text-donkey-warm/85">{t.task}</span><button onClick={() => del(t)} className="text-donkey-grey hover:text-donkey-red" aria-label="Delete task"><Trash2 className="w-4 h-4" /></button></div>)}</div>
        </Card>
      ))}
    </div>
  );
}

function Analytics({ bid }) {
  const [d, setD] = useState(null);
  const [health, setHealth] = useState(null);
  const [cc, setCc] = useState(null);
  const [ue, setUe] = useState(null);
  const [opt, setOpt] = useState(null);
  const [spend, setSpend] = useState(0);
  const [cost, setCost] = useState(0);
  const runUe = async () => { const { data } = await api.post(`/grow/${bid}/unit-economics`, { marketing_spend: Number(spend) || null, delivery_cost_per_customer: Number(cost) || null }); setUe(data); };
  useEffect(() => { (async () => {
    setD((await api.get(`/grow/${bid}/analytics`)).data);
    setHealth((await api.get(`/grow/${bid}/customers/health`)).data);
    setCc((await api.get(`/grow/${bid}/concentration-capacity`)).data);
    setOpt((await api.get(`/grow/${bid}/optimisation`)).data);
    runUe();
  })(); }, [bid]);
  if (!d) return <Loader2 className="w-5 h-5 animate-spin text-donkey-orange" />;
  const srcColor = { first_party: "text-donkey-green", user_entered: "text-donkey-amber", unavailable: "text-donkey-grey", estimate: "text-donkey-orange", verified: "text-donkey-green" };
  return (
    <div className="space-y-4" data-testid="grow-analytics">
      <Card data-testid="analytics-funnel">
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-3">Funnel · {d.date_range}</p>
        <div className="space-y-1.5">
          {d.funnel.map((f, i) => (
            <div key={i} className="flex justify-between text-sm items-center"><span className="text-donkey-warm/85">{f.stage}</span>
              <span className="font-mono">{f.value}<span className={`ml-2 text-[10px] ${srcColor[f.source]}`}>{d.legend[f.source]}</span></span>
            </div>
          ))}
        </div>
        <p className="text-xs text-donkey-grey mt-3">Lead→won {d.lead_conversion_pct.value ?? "Not available"}% · won→customer {d.customer_conversion_pct.value ?? "Not available"}%. Contacts are de-duplicated (no double-counting of raw leads).</p>
      </Card>
      {health && <Card data-testid="analytics-health">
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Customer health</p>
        <div className="flex gap-3 text-sm">{Object.entries(health.counts).map(([k, v]) => <span key={k} className="rounded-full border border-white/15 px-3 py-1 capitalize">{k.replace("_", " ")}: <b>{v}</b></span>)}</div>
        <p className="text-[11px] text-donkey-grey mt-2">{health.note}</p>
      </Card>}
      {cc && <Card data-testid="analytics-concentration">
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Concentration & capacity</p>
        {cc.concentration.warnings.length ? <ul className="space-y-1">{cc.concentration.warnings.map((w, i) => <li key={i} className="text-xs text-donkey-amber flex gap-1.5"><Flame className="w-3.5 h-3.5 shrink-0" />{w}</li>)}</ul> : <p className="text-sm text-donkey-green">No severe concentration flagged.</p>}
        <p className={`text-sm mt-2 ${cc.can_recommend_scale ? "text-donkey-green" : "text-donkey-amber"}`}>Scale {cc.can_recommend_scale ? "possible" : "blocked"} — {cc.gate_note}</p>
        {cc.conditions_before_scaling.length > 0 && <ul className="mt-1 space-y-1">{cc.conditions_before_scaling.map((c, i) => <li key={i} className="text-xs text-donkey-grey">• {c}</li>)}</ul>}
      </Card>}
      {ue && <Card data-testid="analytics-uniteconomics">
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Unit economics</p>
        <div className="flex gap-2 mb-3">
          <Input type="number" value={spend} onChange={(e) => setSpend(e.target.value)} placeholder="Marketing spend £" aria-label="Marketing spend" className="bg-donkey-bg border-white/15 h-9" data-testid="ue-spend" />
          <Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="Delivery cost/customer £" aria-label="Delivery cost per customer" className="bg-donkey-bg border-white/15 h-9" data-testid="ue-cost" />
          <Button onClick={runUe} className="bg-donkey-orange text-donkey-black font-semibold h-9" data-testid="ue-run">Calculate</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          {Object.entries(ue.metrics).map(([k, m]) => (
            <div key={k} className="rounded-lg bg-donkey-bg border border-white/10 p-3" data-testid={`ue-${k}`}>
              <p className="text-xs capitalize text-donkey-grey">{k.replace(/_/g, " ")}</p>
              <p className="font-mono text-donkey-warm">{m.value === null ? "Not available" : m.value}</p>
              <p className={`text-[10px] ${srcColor[m.source] || "text-donkey-grey"}`}>{m.source === "estimate" ? "Scenario / estimate" : m.source}</p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-donkey-amber mt-2">{ue.warning}</p>
      </Card>}
      {opt && <Card data-testid="analytics-optimisation">
        <p className="text-xs uppercase tracking-[0.18em] text-donkey-grey mb-2">Marketing optimisation</p>
        {opt.campaigns.length === 0 && <p className="text-sm text-donkey-grey">No campaigns yet.</p>}
        {opt.campaigns.map((c, i) => (
          <div key={i} className="text-sm border-b border-white/5 py-2">
            <div className="flex justify-between"><span className="text-donkey-warm/85">{c.campaign} · {c.channel}</span><span className="text-donkey-orange capitalize">{c.recommendation.replace(/_/g, " ")}</span></div>
            <p className="text-[11px] text-donkey-grey">{c.why} · leads {c.attributable_leads.value}, customers {c.attributable_customers.value}</p>
          </div>
        ))}
        <p className="text-[11px] text-donkey-grey mt-2">{opt.rule}</p>
      </Card>}
    </div>
  );
}
